import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CUR = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const fmt = (n, cur = "TRY") => `${CUR[cur] || ""}${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");

export default function PurchaseOrderPrint() {
  const { id } = useParams();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/purchase-orders/${id}/detail`);
        setPo(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!po) return <div className="p-10 text-center text-muted-foreground">Sipariş bulunamadı.</div>;

  const items = po.items || [];
  const subtotal = items.reduce((s, it) => s + Number(it.line_total ?? (Number(it.ordered_qty || it.quantity || 0) * Number(it.unit_price || 0))), 0);
  const taxTotal = items.reduce((s, it) => s + Number(it.line_tax ?? 0), 0);
  const total = subtotal + taxTotal;

  return (
    <div className="min-h-screen bg-white text-black">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 15mm; }
        }
        .print-page { max-width: 800px; margin: 0 auto; padding: 24px; font-family: Arial, sans-serif; }
        .print-page h1 { font-size: 22px; margin: 0 0 4px; }
        .print-page table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .print-page th, .print-page td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; vertical-align: top; }
        .print-page th { background: #f3f4f6; font-weight: 600; }
        .print-page .num { text-align: right; }
        .print-page .kv { display: grid; grid-template-columns: 130px 1fr; gap: 4px 12px; font-size: 12px; }
      `}</style>

      <div className="no-print p-3 border-b flex justify-between items-center bg-slate-100">
        <div className="text-sm text-slate-600">Yazdırma Önizleme — {po.po_no}</div>
        <Button size="sm" onClick={() => window.print()} data-testid="print-do-btn">
          <Printer className="w-4 h-4 mr-2" /> Yazdır / PDF olarak Kaydet
        </Button>
      </div>

      <div className="print-page" data-testid="po-print-page">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h1>SATIN ALMA SİPARİŞİ</h1>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{po.po_no}</div>
          </div>
          <div className="kv" style={{ textAlign: "right" }}>
            <div>Sipariş Tarihi:</div><div>{fmtDate(po.created_at)}</div>
            <div>Teslim Tarihi:</div><div>{fmtDate(po.expected_delivery_date)}</div>
            <div>Durum:</div><div>{po.status}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div style={{ border: "1px solid #d1d5db", padding: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>TEDARİKÇİ</div>
            <div className="kv">
              <div>Firma:</div><div style={{ fontWeight: 600 }}>{po.supplier_name || "—"}</div>
              <div>Yetkili:</div><div>{po.supplier_contact || "—"}</div>
              <div>Telefon:</div><div>{po.supplier_phone || "—"}</div>
              <div>E-posta:</div><div>{po.supplier_email || "—"}</div>
            </div>
          </div>
          <div style={{ border: "1px solid #d1d5db", padding: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>TESLİMAT</div>
            <div className="kv">
              <div>Alıcı:</div><div style={{ fontWeight: 600 }}>FUCAN Personel Giyim</div>
              <div>Adres:</div><div>Fabrika / İstanbul</div>
              <div>Not:</div><div>{po.delivery_notes || "—"}</div>
            </div>
          </div>
        </div>

        <table style={{ marginBottom: 16 }}>
          <thead>
            <tr>
              <th style={{ width: 30 }}>#</th>
              <th>Kod</th>
              <th>Marka</th>
              <th>Ürün / Malzeme</th>
              <th>Varyant</th>
              <th className="num">Miktar</th>
              <th>Birim</th>
              <th className="num">Br. Fiyat</th>
              <th>PB</th>
              <th className="num">İsk %</th>
              <th className="num">KDV %</th>
              <th className="num">Toplam</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const qty = Number(it.ordered_qty || it.quantity || 0);
              const up = Number(it.unit_price || 0);
              const disc = Number(it.discount_percent || 0);
              const line = Number(it.line_total ?? (qty * up * (1 - disc / 100)));
              return (
                <tr key={it.id || i}>
                  <td>{i + 1}</td>
                  <td>{it.variant_sku || it.material_code || "—"}</td>
                  <td>{it.brand_name || "—"}</td>
                  <td>{it.product_name || it.material_name || "—"}</td>
                  <td>{it.variant_sku || "—"}</td>
                  <td className="num">{qty.toLocaleString("tr-TR")}</td>
                  <td>{it.unit || ""}</td>
                  <td className="num">{fmt(up, it.currency)}</td>
                  <td>{it.currency || "TRY"}</td>
                  <td className="num">{disc || 0}</td>
                  <td className="num">{it.tax_rate || 0}</td>
                  <td className="num">{fmt(line, it.currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <table style={{ width: 320 }}>
            <tbody>
              <tr><td>Ara Toplam</td><td className="num">{fmt(subtotal)}</td></tr>
              <tr><td>KDV Toplam</td><td className="num">{fmt(taxTotal)}</td></tr>
              <tr style={{ fontWeight: 700, background: "#f3f4f6" }}>
                <td>GENEL TOPLAM</td><td className="num">{fmt(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {po.order_notes && (
          <div style={{ marginBottom: 12, fontSize: 12 }}>
            <div style={{ fontWeight: 600 }}>Sipariş Notu:</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{po.order_notes}</div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, marginTop: 40, fontSize: 12 }}>
          <div>
            <div style={{ borderTop: "1px solid #6b7280", paddingTop: 6 }}>Alıcı — İmza / Kaşe</div>
          </div>
          <div>
            <div style={{ borderTop: "1px solid #6b7280", paddingTop: 6 }}>Tedarikçi — İmza / Kaşe</div>
          </div>
        </div>
      </div>
    </div>
  );
}
